import { takeEvery, select, fork, call, takeLatest } from 'redux-saga/effects'
import { init, setUserId, track, reset, identify, Identify } from '@amplitude/analytics-browser'

import * as settings from 'settings'
import { LOCATION_CHANGE, pathSelector } from 'browser-location'
import { accountKeySelector } from 'auth/selectors'
import { SET_AUTHENTICATED_USER, LOGOUT } from 'auth/constants'
import { cookiesAcceptedSelector } from 'cookies/selectors'
import { SET_COOKIES } from 'cookies/constants'
import { deleteCookie } from 'cookies/utils'
import { CONFIGURATION_RECEIVED } from 'wordpress/constants'
import { RD_ANALYTICS_ID } from 'analytics/constants'
import { initializeGoogleTagManager, initializeGTagTrackMethod } from 'analytics/googleTagManager'
import { setAmplitudeInitialized, isAmplitudeInitialized } from 'analytics/utils'

import * as constants from './constants'
import * as api from './api'

export const getCookie = name => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
        return parts.pop().split(';').shift()
    }
    return null
}

const initRD = url => {
    const elem = document.getElementById(RD_ANALYTICS_ID)

    if (!elem) {
        const s = document.createElement('script')
        s.type = 'text/javascript'
        s.src = url
        s.async = 1
        s.id = RD_ANALYTICS_ID

        document.body.appendChild(s)
    }
}

const syncAmplitudeAuthState = accountKey => {
    if (!isAmplitudeInitialized()) return

    if (accountKey) {
        setUserId(accountKey)
        identify(new Identify().set('is_logged_in', true))
    } else {
        identify(new Identify().set('is_logged_in', false))
    }
}

function* trackPageView() {
    const path = yield select(pathSelector)
    if (window.gtag) {
        window.gtag('event', 'page_view', { page_path: path })
    }

    if (isAmplitudeInitialized()) {
        track('Page View', { path })
    }
}

function* watchLocationChangeSaga() {
    yield takeEvery(LOCATION_CHANGE, trackPageView)
}

function* setUserIdTrackerSaga() {
    yield takeEvery(SET_AUTHENTICATED_USER, function* () {
        const accountKey = yield select(accountKeySelector)
        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)

        syncAmplitudeAuthState(accountKey)

        //gtag
        if (!window.gtag) return

        window.gtag('config', tagManagerId, {
            user_id: accountKey,
        })

        //gtm
        if (tagManagerId) {
            window.dataLayer.push({ userId: accountKey, event: 'authentication' })
        }
    })
}

function* logoutTrackerSaga() {
    yield takeEvery(LOGOUT, function* () {
        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)

        if (isAmplitudeInitialized()) {
            reset()
            const identifyEvent = new Identify().set('is_logged_in', false)
            identify(identifyEvent)
        }

        if (window.gtag) {
            window.gtag('config', tagManagerId, {
                user_id: null,
            })
        }
    })
}

function* analyticsSaga() {
    const cookiesAccepted = yield select(cookiesAcceptedSelector)
    const accountKey = yield select(accountKeySelector)
    if (cookiesAccepted?.analytics) {
        // Google
        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)

        if (tagManagerId) {
            yield call(initializeGoogleTagManager, tagManagerId)
            initializeGTagTrackMethod(tagManagerId)
        }

        yield call(trackPageView)

        const rdAnalyticsUrl = yield select(settings.selectors.rdAnalyticsUrlSelector)
        const amplitudeApiKey = (yield select(settings.selectors.amplitudeApiKeySelector)) || window.AMPLITUDE_API_KEY

        // RD station
        if (rdAnalyticsUrl) {
            initRD(rdAnalyticsUrl)
        }

        if (amplitudeApiKey && !isAmplitudeInitialized()) {
            init(amplitudeApiKey, { serverZone: window.AMPLITUDE_SERVER_LOCATION || 'EU' })
            setAmplitudeInitialized(true)
        }

        syncAmplitudeAuthState(accountKey)
    } else if (!cookiesAccepted?.analytics) {
        if (window.gtag) window.gtag('remove')
        deleteCookie('_gtag')
        deleteCookie('_gid')
        deleteCookie('_gat')

        // Remove RD analytics
        const elem = document.getElementById(RD_ANALYTICS_ID)
        if (elem) {
            elem.parentNode.removeChild(elem)
        }

        if (isAmplitudeInitialized()) {
            reset()
            setAmplitudeInitialized(false)
        }
    }
}

function* googleReserveConversionSaga() {
    yield takeEvery(constants.SEND_GOOGLE_RESERVE_CONVERSION_DATA, function* ({ currentLocationId }) {
        const rwgToken = getCookie('_rwg_token')
        const merchantId = getCookie('_merchant_id')
        const merchantChanged = merchantId === currentLocationId ? 2 : 1

        if (rwgToken) {
            yield call(api.sendGoogleReserveConversionData, rwgToken, merchantChanged)
        }
    })
}

export default function* rootSaga() {
    yield fork(watchLocationChangeSaga)
    yield fork(setUserIdTrackerSaga)
    yield fork(logoutTrackerSaga)
    yield fork(googleReserveConversionSaga)
    yield takeEvery(SET_COOKIES, analyticsSaga)
    yield takeLatest(CONFIGURATION_RECEIVED, analyticsSaga)
}
